// ============================================================================
// ecgAnalysis.ts — РАЗБОР СНИМКА ЭКГ (единая точка клиента).
//
// Снимок уходит на СЕРВЕР платформы (EcgService.AnalyzeEcg): сервер сам
// обращается к сервису-анализатору. Клиент НЕ ходит на localhost — на телефоне
// это был бы сам телефон, а https-страница заблокировала бы http.
//
// Модуль используется и экраном врача (pages/EcgAnalysisPage.tsx), и офлайн-
// очередью (lib/ecgOfflineQueue.ts): снимок, снятый без связи, разбирается
// ТЕМ ЖЕ кодом при восстановлении связи — без дублей и расхождений.
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { errorCode, friendlyError, isTransportError } from '@/lib/errors';

/**
 * Предел ожидания ответа сервера. Сервер вызывает анализатор со своим
 * таймаутом (по умолчанию 120 с), поэтому клиентский дедлайн ВЫШЕ него:
 * иначе клиент обрывает запрос одновременно с сервером и пользователь видит
 * общий таймаут вместо точной причины («анализатор недоступен»).
 */
export const ECG_ANALYZE_TIMEOUT_MS = 130_000;

/** Тот же предел, что и на сервере (handler/ecg.go): больше — явная ошибка. */
export const ECG_MAX_IMAGE_BYTES = 25 << 20;

/**
 * ecgFileIssue — проверка выбранного файла ДО сети (тот же предел, что на
 * сервере): пустой/слишком большой/заведомо не изображение. Пустая строка —
 * файл допустим. Ошибка явная, без «отправим и посмотрим».
 */
export function ecgFileIssue(file: File): string {
    if (file.size === 0) return 'Файл пустой — выберите изображение ЭКГ';
    if (file.size > ECG_MAX_IMAGE_BYTES) {
        return `Изображение ЭКГ больше ${ECG_MAX_IMAGE_BYTES >> 20} МБ — уменьшите снимок или сделайте фото заново`;
    }
    const type = (file.type || '').toLowerCase();
    if (type && !type.startsWith('image/')) {
        return `Ожидается изображение ЭКГ (JPEG/PNG), выбран файл ${type}`;
    }
    return '';
}

/**
 * isAnalyzeTimeout — сервер не ответил за отведённое время (connect
 * Code.DeadlineExceeded = 4). Это НЕ потеря связи: запрос мог дойти до
 * сервера, поэтому в офлайн-очередь его не кладём — показываем явную ошибку.
 */
export function isAnalyzeTimeout(error: unknown): boolean {
    const code = errorCode(error);
    if (code === 4) return true;
    if (typeof code !== 'string') return false;
    const name = code.toLowerCase();
    return name === '4' || name === 'deadline_exceeded' || name === 'deadlineexceeded';
}

/** Этап двухэтапной оценки MI: порог, вероятность и флаг срабатывания. */
export interface EcgMiStage {
    probability: number | null;
    threshold: number | null;
    flag: boolean;
}

/** Этап подтверждения MI: дополнительно — прошли ли ST-критерии. */
export interface EcgMiConfirmStage extends EcgMiStage {
    rulesPositive: boolean | null;
}

/** Сигнатура ST-детектора (ecg-analyzer/app/model/st_detection.py). */
export interface EcgStCriteria {
    evaluated: boolean;
    reason: string;
    elevation: boolean;
    depression: boolean;
    positive: boolean;
    referenceLead: string;
    elevationLeads: string[];
    depressionLeads: string[];
    /** Смещение ST на J+60 мс по отведениям (мВ). */
    valuesMv: Record<string, number>;
    valuesJ80Mv: Record<string, number>;
    thresholdsMv: Record<string, number>;
    depressionThresholdMv: number | null;
    criteria: string;
}

/** Результат одного амплитудного критерия гипертрофии. */
export interface EcgHypCriterionResult {
    passed: boolean;
    valueMv: number | null;
    thresholdMv: number | null;
}

/** Сигнатура амплитудных критериев гипертрофии (ecg-analyzer/app/model/hyp_criteria.py). */
export interface EcgHypCriteria {
    evaluated: boolean;
    reason: string;
    sokolow: boolean;
    cornell: boolean;
    peguero: boolean;
    rvh: boolean;
    anyLvh: boolean;
    anyHyp: boolean;
    values: Record<string, number | null>;
    /** Только критерии, реально присутствующие в ответе. */
    results: Record<string, EcgHypCriterionResult>;
    sexApplied: boolean;
    axisDeg: number | null;
    qrsMs: number | null;
}

/** Аддитивный блок `mi_hyp` ответа анализатора (статус и оба класса). */
export interface EcgMiHypBlock {
    status: string;
    reason: string;
    mi: {
        phase: string;
        conflict: boolean;
        conflictNote: string;
        screening: EcgMiStage | null;
        confirm: EcgMiConfirmStage | null;
        st: EcgStCriteria | null;
    } | null;
    hyp: {
        source: string;
        conflict: boolean;
        conflictNote: string;
        probability: number | null;
        threshold: number | null;
        flag: boolean;
        criteria: EcgHypCriteria | null;
    } | null;
}

export interface EcgFinding {
    code: string;
    /** Калиброванная вероятность (её анализатор отдаёт как `prob`). */
    probability: number | null;
    /** Вероятность до калибровки (`prob_raw`) — если анализатор её передал. */
    probabilityRaw: number | null;
    /** Порог обнаружения для этого кода (`threshold`) — если передан. */
    threshold: number | null;
    /** Разница вероятности и порога в долях (не в процентных пунктах). */
    thresholdMargin: number | null;
    /** Источник находки: `model` или `rr_heuristic` — если сервис его передал. */
    source: string;
    /** Прошла ли вероятность калибровку (null — сервис не сообщил). */
    calibrated: boolean | null;
    /** Фаза двухэтапной оценки MI: `screening` | `confirmed` — если передана. */
    phase: string;
    /** Пояснение сервиса к находке (например, «Не диагноз…»). */
    confidenceNote: string;
    /** Замечание о расхождении модели и правил — если сервис его передал. */
    conflictNote: string;
    /** Скрининговый этап MI (порог/вероятность/флаг). */
    miScreening: EcgMiStage | null;
    /** Этап подтверждения MI (с правилами ST). */
    miConfirm: EcgMiConfirmStage | null;
    /** Источник HYP: `nn` | `both` | `rules` | `conflict` — если передан. */
    hypSource: string;
    /** Амплитудные критерии HYP из ответа. */
    hypCriteria: EcgHypCriteria | null;
    /** ST-детектор, приложенный к MI-находке. */
    st: EcgStCriteria | null;
}

/** Находка без MI/HYP-полей (например, из блока объяснимости). */
export function emptyFindingExtras(): Pick<EcgFinding,
    'phase' | 'confidenceNote' | 'conflictNote' | 'miScreening' | 'miConfirm'
    | 'hypSource' | 'hypCriteria' | 'st'> {
    return {
        phase: '',
        confidenceNote: '',
        conflictNote: '',
        miScreening: null,
        miConfirm: null,
        hypSource: '',
        hypCriteria: null,
        st: null,
    };
}

export interface EcgQuality {
    verdict: string;
    leadsExtracted: number | null;
    pxPerMm: number | null;
    calibrationFound: boolean | null;
    /** Числовые метрики качества снимка (0..1) — если анализатор их передал. */
    sharpness: number | null;
    exposure: number | null;
    coverage: number | null;
}

export interface EcgMeasurements {
    hrBpm: number | null;
    prMs: number | null;
    qrsMs: number | null;
    qtMs: number | null;
    qtcMs: number | null;
    axisDeg: number | null;
    /** Что из измерений реально рассчитано (`availability`); пусто — не сообщено. */
    availability: Record<string, boolean>;
}

/** Прослеженное отведение: имя и длительность записи (сигнал не храним). */
export interface EcgSignalLead {
    name: string;
    durationS: number | null;
}

export interface EcgSignalInfo {
    fsHz: number | null;
    durationS: number | null;
    leads: EcgSignalLead[];
    previewPngBase64: string;
}

export interface EcgQualityGate {
    passed: boolean | null;
    failed: string[];
}

export interface EcgSignalGate {
    ok: boolean | null;
    reasons: string[];
    warnings: string[];
    metrics: Record<string, number>;
}

/** Сведения модели находок: вероятности по классам, пороги, калибровка, допуск. */
export interface EcgModelInfo {
    status: string;
    reason: string;
    calibration: string;
    inferenceMs: number | null;
    /** Время атрибуции в ответе (если сервис его передал); не влияет на вывод. */
    attributionMs: number | null;
    checkpoint: string;
    sha256: string;
    thresholds: Record<string, number>;
    probabilities: Record<string, number>;
    probabilitiesRaw: Record<string, number>;
    qualityGate: EcgQualityGate;
    signalGate: EcgSignalGate;
}

export interface EcgCriterionReference {
    low: number | null;
    high: number | null;
    lowInclusive: boolean;
    highInclusive: boolean;
}

/** Справочный критерий измерения: значение, порог и отклонение. */
export interface EcgCriterion {
    code: string;
    label: string;
    category: string;
    measurement: string;
    value: number | null;
    unit: string;
    reference: EcgCriterionReference;
    status: 'within' | 'below' | 'above' | 'unknown';
    deviation: number | null;
    deviationAbs: number | null;
    deviationUnit: string;
    note: string;
    explanation: string;
}

export interface EcgMeasurementSummary {
    total: number;
    available: string[];
    missing: string[];
    availableCount: number;
    values: Record<string, number | null>;
    labels: Record<string, string>;
    units: Record<string, string>;
}

export interface EcgSignalQualityExplanation {
    verdict: string;
    metrics: {
        sharpness: number | null;
        exposure: number | null;
        coverage: number | null;
    };
    calibrationFound: boolean;
    pxPerMm: number | null;
    leads: {
        detected: number;
        traced: number;
        expected: number;
        missing: string[];
        short: string[];
        durationsS: Record<string, number>;
    };
    duration: {
        actualMinS: number | null;
        actualMaxS: number | null;
        requiredS: number | null;
        shortfallS: number | null;
    };
    modelInput: {
        adequate: boolean;
        zeroPadded: boolean;
        missingLeads: string[];
        shortLeads: string[];
        reasons: string[];
    };
    adequateForModel: boolean;
    reasons: string[];
    warnings: string[];
    minimumModelLeads: number;
}

export interface EcgConfidenceComponent {
    code: string;
    weight: number;
    score: number;
    value: string | number | boolean | null;
    detail: string;
}

export interface EcgConfidence {
    version: string;
    method: string;
    validated: boolean;
    meaning: string;
    level: 'high' | 'medium' | 'low' | 'insufficient' | string;
    score: number;
    rawScore: number;
    components: EcgConfidenceComponent[];
    caps: string[];
    limitations: string[];
}

export interface EcgAttributionLead {
    index: number;
    lead: string;
    signed: number;
    abs: number;
    share: number;
}

export interface EcgAttributionSegment {
    index: number;
    startMs: number;
    endMs: number;
    signed: number;
    abs: number;
    share: number;
}

export interface EcgOcclusionCheck {
    leadsTop1Match: boolean;
    leadsTop3Overlap: number;
    leadsRankCorrelation: number | null;
    timeTop1Match: boolean;
    timeTop3Overlap: number;
    timeRankCorrelation: number | null;
}

export interface EcgAttributionFinding {
    code: string;
    available: boolean;
    classIndex: number | null;
    probability: number | null;
    method: string;
    baseline: string;
    steps: number;
    signedScore: number | null;
    logitDelta: number | null;
    probabilityDeltaRaw: number | null;
    completenessError: number | null;
    normalized: boolean;
    degenerate: boolean;
    leads: EcgAttributionLead[];
    timeSegments: EcgAttributionSegment[];
    topLeads: EcgAttributionLead[];
    topTimeSegments: EcgAttributionSegment[];
    occlusionCheck: EcgOcclusionCheck | null;
    fallbackReason: string;
    reason: string;
}

export interface EcgAttribution {
    available: boolean;
    version: string;
    method: string;
    baseline: string;
    steps: number;
    normalized: boolean;
    degenerate: boolean;
    signedScore: number | null;
    completeness: { meanAbsError: number | null; maxAbsError: number | null } | null;
    leads: EcgAttributionLead[];
    timeSegments: EcgAttributionSegment[];
    topLeads: EcgAttributionLead[];
    topTimeSegments: EcgAttributionSegment[];
    findings: EcgAttributionFinding[];
    reason: string;
}

export interface EcgEvidenceItem {
    source: string;
    code: string;
    text: string;
    available: boolean;
    status?: string;
    method?: string;
    probability?: number;
    threshold?: number;
    thresholdMargin?: number;
    topLeads?: EcgAttributionLead[];
    topTimeSegments?: EcgAttributionSegment[];
    occlusionCheck?: EcgOcclusionCheck;
}

export interface EcgFindingRationale {
    code: string;
    available: boolean;
    summary: string;
    evidence: EcgEvidenceItem[];
    reason: string;
}

export interface EcgRationale {
    version: string;
    available: boolean;
    summary: string;
    evidence: EcgEvidenceItem[];
    findings: EcgFindingRationale[];
    reason: string;
}

/** Детерминированный слой доказательств сервиса разбора. */
export interface EcgExplainability {
    version: string;
    criteriaVersion: string;
    confidenceVersion: string;
    attributionVersion: string;
    rationaleVersion: string;
    kind: string;
    attributionKind: string;
    diagnosis: boolean;
    status: string;
    reason: string;
    criteria: EcgCriterion[];
    measurements: EcgMeasurementSummary;
    signalQuality: EcgSignalQualityExplanation;
    confidence: EcgConfidence;
    attribution: EcgAttribution;
    rationale: EcgRationale;
    findings: EcgFinding[];
    limitations: string[];
    requiredModelInput: { leads: number | null; durationS: number | null };
}

export interface EcgAnalysis {
    status: string;
    reason: string;
    quality: EcgQuality;
    previewPngBase64: string;
    signal: EcgSignalInfo;
    measurements: EcgMeasurements;
    findings: EcgFinding[];
    /** Блок `model` ответа анализатора; отсутствует — объяснения от модели нет. */
    model: EcgModelInfo | null;
    /** Критерии, качество сигнала, уверенность и атрибуция (аддитивный слой). */
    explainability: EcgExplainability | null;
    /** Аддитивный блок `mi_hyp` (двухэтапная оценка MI и правила HYP). */
    miHyp: EcgMiHypBlock | null;
    pipelineVersion: string;
}

interface JsonObject {
    [key: string]: unknown;
}

function isObject(value: unknown): value is JsonObject {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function strOf(obj: JsonObject, key: string): string {
    const value = obj[key];
    return typeof value === 'string' ? value : '';
}

function numOf(obj: JsonObject, key: string): number | null {
    const value = obj[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function boolOf(obj: JsonObject, key: string): boolean | null {
    const value = obj[key];
    return typeof value === 'boolean' ? value : null;
}

/** Числовая карта (вероятности/пороги по кодам); нечисловые значения отбрасываются. */
function numMapOf(obj: JsonObject, key: string): Record<string, number> {
    const value = obj[key];
    const result: Record<string, number> = {};
    if (!isObject(value)) return result;
    for (const [name, raw] of Object.entries(value)) {
        if (typeof raw === 'number' && Number.isFinite(raw)) result[name] = raw;
    }
    return result;
}

function boolMapOf(obj: JsonObject, key: string): Record<string, boolean> {
    const value = obj[key];
    const result: Record<string, boolean> = {};
    if (!isObject(value)) return result;
    for (const [name, raw] of Object.entries(value)) {
        if (typeof raw === 'boolean') result[name] = raw;
    }
    return result;
}

function stringArrayOf(obj: JsonObject, key: string): string[] {
    const value = obj[key];
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is string => typeof item === 'string');
}

function parseSignal(raw: JsonObject): EcgSignalInfo {
    const leadsRaw = Array.isArray(raw.leads) ? raw.leads.filter(isObject) : [];
    const leads: EcgSignalLead[] = [];
    for (const lead of leadsRaw) {
        const name = strOf(lead, 'name');
        if (!name) continue;
        // Полный массив отсчётов (`signal`) намеренно не разбираем и не храним.
        leads.push({ name, durationS: numOf(lead, 'duration_s') });
    }
    return {
        fsHz: numOf(raw, 'fs_hz'),
        durationS: numOf(raw, 'duration_s'),
        leads,
        previewPngBase64: strOf(raw, 'preview_png_b64'),
    };
}

function parseModel(raw: JsonObject): EcgModelInfo | null {
    const model = raw.model;
    if (!isObject(model)) return null;
    const qualityGate = isObject(model.quality_gate) ? model.quality_gate : {};
    const signalGate = isObject(model.signal_gate) ? model.signal_gate : {};
    return {
        status: strOf(model, 'status'),
        reason: strOf(model, 'reason'),
        calibration: strOf(model, 'calibration'),
        inferenceMs: numOf(model, 'inference_ms'),
        attributionMs: numOf(model, 'attribution_ms'),
        checkpoint: strOf(model, 'checkpoint'),
        sha256: strOf(model, 'sha256'),
        thresholds: numMapOf(model, 'thresholds'),
        probabilities: numMapOf(model, 'probabilities'),
        probabilitiesRaw: numMapOf(model, 'probabilities_raw'),
        qualityGate: {
            passed: boolOf(qualityGate, 'passed'),
            failed: stringArrayOf(qualityGate, 'failed'),
        },
        signalGate: {
            ok: boolOf(signalGate, 'ok'),
            reasons: stringArrayOf(signalGate, 'reasons'),
            warnings: stringArrayOf(signalGate, 'warnings'),
            metrics: numMapOf(signalGate, 'metrics'),
        },
    };
}

function parseMiStage(raw: unknown): EcgMiStage | null {
    if (!isObject(raw)) return null;
    return {
        probability: numOf(raw, 'prob'),
        threshold: numOf(raw, 'threshold'),
        flag: boolOf(raw, 'flag') ?? false,
    };
}

function parseMiConfirmStage(raw: unknown): EcgMiConfirmStage | null {
    if (!isObject(raw)) return null;
    return {
        probability: numOf(raw, 'prob'),
        threshold: numOf(raw, 'threshold'),
        flag: boolOf(raw, 'flag') ?? false,
        rulesPositive: boolOf(raw, 'rules_positive'),
    };
}

function parseStCriteria(raw: unknown): EcgStCriteria | null {
    if (!isObject(raw)) return null;
    return {
        evaluated: boolOf(raw, 'evaluated') ?? false,
        reason: strOf(raw, 'reason'),
        elevation: boolOf(raw, 'st_elevation') ?? false,
        depression: boolOf(raw, 'st_depression') ?? false,
        positive: boolOf(raw, 'positive') ?? false,
        referenceLead: strOf(raw, 'reference_lead'),
        elevationLeads: stringArrayOf(raw, 'elevation_leads'),
        depressionLeads: stringArrayOf(raw, 'depression_leads'),
        valuesMv: numMapOf(raw, 'values_mv'),
        valuesJ80Mv: numMapOf(raw, 'values_j80_mv'),
        thresholdsMv: numMapOf(raw, 'thresholds_mv'),
        depressionThresholdMv: numOf(raw, 'depression_threshold_mv'),
        criteria: strOf(raw, 'criteria'),
    };
}

function parseHypCriterionResult(
    raw: unknown,
    valueKey: string,
    thresholdKey: string,
): EcgHypCriterionResult | null {
    if (!isObject(raw)) return null;
    return {
        passed: boolOf(raw, 'passed') ?? false,
        valueMv: numOf(raw, valueKey),
        thresholdMv: numOf(raw, thresholdKey),
    };
}

function parseHypCriteria(raw: unknown): EcgHypCriteria | null {
    if (!isObject(raw)) return null;
    const criteria = isObject(raw.criteria) ? raw.criteria : {};
    const results: Record<string, EcgHypCriterionResult> = {};
    const definitions: Array<[string, string, string]> = [
        ['sokolow', 'value_mv', 'threshold_mv'],
        ['cornell', 'value_mv', 'threshold_mv'],
        ['peguero', 'value_mv', 'threshold_mv'],
        ['rvh', 's_v5_r_v1_mv', 'threshold_s_v5_r_v1_mv'],
    ];
    for (const [code, valueKey, thresholdKey] of definitions) {
        const parsed = parseHypCriterionResult(criteria[code], valueKey, thresholdKey);
        if (parsed) results[code] = parsed;
    }
    return {
        evaluated: boolOf(raw, 'evaluated') ?? false,
        reason: strOf(raw, 'reason'),
        sokolow: boolOf(raw, 'sokolow') ?? false,
        cornell: boolOf(raw, 'cornell') ?? false,
        peguero: boolOf(raw, 'peguero') ?? false,
        rvh: boolOf(raw, 'rvh') ?? false,
        anyLvh: boolOf(raw, 'any_lvh') ?? false,
        anyHyp: boolOf(raw, 'any_hyp') ?? false,
        values: nullableNumMapOf(raw, 'values'),
        results,
        sexApplied: boolOf(raw, 'sex_applied') ?? false,
        axisDeg: numOf(raw, 'axis_deg'),
        qrsMs: numOf(raw, 'qrs_ms'),
    };
}

function parseMiHypBlock(raw: JsonObject): EcgMiHypBlock | null {
    const block = raw.mi_hyp;
    if (!isObject(block)) return null;
    const miRaw = isObject(block.mi) ? block.mi : null;
    const hypRaw = isObject(block.hyp) ? block.hyp : null;
    const nn = hypRaw && isObject(hypRaw.nn) ? hypRaw.nn : null;
    return {
        status: strOf(block, 'status'),
        reason: strOf(block, 'reason'),
        mi: miRaw
            ? {
                phase: strOf(miRaw, 'phase'),
                conflict: boolOf(miRaw, 'conflict') ?? false,
                conflictNote: strOf(miRaw, 'conflict_note'),
                screening: parseMiStage(miRaw.screening),
                confirm: parseMiConfirmStage(miRaw.confirm),
                st: parseStCriteria(miRaw.st),
            }
            : null,
        hyp: hypRaw
            ? {
                source: strOf(hypRaw, 'source'),
                conflict: boolOf(hypRaw, 'conflict') ?? false,
                conflictNote: strOf(hypRaw, 'conflict_note'),
                probability: nn ? numOf(nn, 'prob') : null,
                threshold: nn ? numOf(nn, 'threshold') : null,
                flag: boolOf(hypRaw, 'flag') ?? false,
                criteria: parseHypCriteria(hypRaw.rules),
            }
            : null,
    };
}

function nullableNumMapOf(obj: JsonObject, key: string): Record<string, number | null> {
    const value = obj[key];
    const result: Record<string, number | null> = {};
    if (!isObject(value)) return result;
    for (const [name, raw] of Object.entries(value)) {
        if (raw === null) result[name] = null;
        else if (typeof raw === 'number' && Number.isFinite(raw)) result[name] = raw;
    }
    return result;
}

function numberOrNull(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function booleanOrNull(value: unknown): boolean | null {
    return typeof value === 'boolean' ? value : null;
}

function criterionStatus(value: unknown): EcgCriterion['status'] {
    if (value === 'within' || value === 'below' || value === 'above' || value === 'unknown') {
        return value;
    }
    return 'unknown';
}

function parseCriteria(raw: JsonObject): EcgCriterion[] {
    const items = Array.isArray(raw.criteria) ? raw.criteria.filter(isObject) : [];
    return items.map((item) => {
        const reference = isObject(item.reference) ? item.reference : {};
        return {
            code: strOf(item, 'code'),
            label: strOf(item, 'label'),
            category: strOf(item, 'category'),
            measurement: strOf(item, 'measurement'),
            value: numOf(item, 'value'),
            unit: strOf(item, 'unit'),
            reference: {
                low: numOf(reference, 'low'),
                high: numOf(reference, 'high'),
                lowInclusive: boolOf(reference, 'low_inclusive') ?? true,
                highInclusive: boolOf(reference, 'high_inclusive') ?? true,
            },
            status: criterionStatus(item.status),
            deviation: numOf(item, 'deviation'),
            deviationAbs: numOf(item, 'deviation_abs'),
            deviationUnit: strOf(item, 'deviation_unit'),
            note: strOf(item, 'note'),
            explanation: strOf(item, 'explanation'),
        };
    });
}

function parseMeasurementSummary(raw: JsonObject): EcgMeasurementSummary {
    const summary = isObject(raw.measurements) ? raw.measurements : {};
    return {
        total: numOf(summary, 'total') ?? 0,
        available: stringArrayOf(summary, 'available'),
        missing: stringArrayOf(summary, 'missing'),
        availableCount: numOf(summary, 'available_count') ?? 0,
        values: nullableNumMapOf(summary, 'values'),
        labels: stringMapOf(summary, 'labels'),
        units: stringMapOf(summary, 'units'),
    };
}

function stringMapOf(obj: JsonObject, key: string): Record<string, string> {
    const value = obj[key];
    const result: Record<string, string> = {};
    if (!isObject(value)) return result;
    for (const [name, raw] of Object.entries(value)) {
        if (typeof raw === 'string') result[name] = raw;
    }
    return result;
}

function parseSignalQuality(raw: JsonObject): EcgSignalQualityExplanation {
    const quality = isObject(raw.signal_quality) ? raw.signal_quality : {};
    const metrics = isObject(quality.metrics) ? quality.metrics : {};
    const leads = isObject(quality.leads) ? quality.leads : {};
    const duration = isObject(quality.duration) ? quality.duration : {};
    const modelInput = isObject(quality.model_input) ? quality.model_input : {};
    return {
        verdict: strOf(quality, 'verdict'),
        metrics: {
            sharpness: numOf(metrics, 'sharpness'),
            exposure: numOf(metrics, 'exposure'),
            coverage: numOf(metrics, 'coverage'),
        },
        calibrationFound: boolOf(quality, 'calibration_found') ?? false,
        pxPerMm: numOf(quality, 'px_per_mm'),
        leads: {
            detected: numOf(leads, 'detected') ?? 0,
            traced: numOf(leads, 'traced') ?? 0,
            expected: numOf(leads, 'expected') ?? 12,
            missing: stringArrayOf(leads, 'missing'),
            short: stringArrayOf(leads, 'short'),
            durationsS: numMapOf(leads, 'durations_s'),
        },
        duration: {
            actualMinS: numOf(duration, 'actual_min_s'),
            actualMaxS: numOf(duration, 'actual_max_s'),
            requiredS: numOf(duration, 'required_s'),
            shortfallS: numOf(duration, 'shortfall_s'),
        },
        modelInput: {
            adequate: boolOf(modelInput, 'adequate') ?? false,
            zeroPadded: boolOf(modelInput, 'zero_padded') ?? false,
            missingLeads: stringArrayOf(modelInput, 'missing_leads'),
            shortLeads: stringArrayOf(modelInput, 'short_leads'),
            reasons: stringArrayOf(modelInput, 'reasons'),
        },
        adequateForModel: boolOf(quality, 'adequate_for_model') ?? false,
        reasons: stringArrayOf(quality, 'reasons'),
        warnings: stringArrayOf(quality, 'warnings'),
        minimumModelLeads: numOf(quality, 'minimum_model_leads') ?? 8,
    };
}

function parseConfidence(raw: JsonObject): EcgConfidence {
    const confidence = isObject(raw.confidence) ? raw.confidence : {};
    const componentsRaw = Array.isArray(confidence.components) ? confidence.components.filter(isObject) : [];
    return {
        version: strOf(confidence, 'version'),
        method: strOf(confidence, 'method'),
        validated: boolOf(confidence, 'validated') ?? false,
        meaning: strOf(confidence, 'meaning'),
        level: strOf(confidence, 'level') || 'insufficient',
        score: numOf(confidence, 'score') ?? 0,
        rawScore: numOf(confidence, 'raw_score') ?? 0,
        components: componentsRaw.map((item) => ({
            code: strOf(item, 'code'),
            weight: numOf(item, 'weight') ?? 0,
            score: numOf(item, 'score') ?? 0,
            value: typeof item.value === 'string' || typeof item.value === 'number' || typeof item.value === 'boolean'
                ? item.value
                : null,
            detail: strOf(item, 'detail'),
        })),
        caps: stringArrayOf(confidence, 'caps'),
        limitations: stringArrayOf(confidence, 'limitations'),
    };
}

function objectsOf(obj: JsonObject, key: string): JsonObject[] {
    const value = obj[key];
    return Array.isArray(value) ? value.filter(isObject) : [];
}

function parseAttributionLead(item: JsonObject): EcgAttributionLead {
    return {
        index: numOf(item, 'index') ?? 0,
        lead: strOf(item, 'lead'),
        signed: numOf(item, 'signed') ?? 0,
        abs: numOf(item, 'abs') ?? 0,
        share: numOf(item, 'share') ?? 0,
    };
}

function parseAttributionSegment(item: JsonObject): EcgAttributionSegment {
    return {
        index: numOf(item, 'index') ?? 0,
        startMs: numOf(item, 'start_ms') ?? 0,
        endMs: numOf(item, 'end_ms') ?? 0,
        signed: numOf(item, 'signed') ?? 0,
        abs: numOf(item, 'abs') ?? 0,
        share: numOf(item, 'share') ?? 0,
    };
}

function parseOcclusionCheck(value: unknown): EcgOcclusionCheck | null {
    if (!isObject(value)) return null;
    return {
        leadsTop1Match: boolOf(value, 'leads_top1_match') ?? false,
        leadsTop3Overlap: numOf(value, 'leads_top3_overlap') ?? 0,
        leadsRankCorrelation: numberOrNull(value.leads_rank_correlation),
        timeTop1Match: boolOf(value, 'time_top1_match') ?? false,
        timeTop3Overlap: numOf(value, 'time_top3_overlap') ?? 0,
        timeRankCorrelation: numberOrNull(value.time_rank_correlation),
    };
}

function parseAttributionFinding(item: JsonObject): EcgAttributionFinding {
    return {
        code: strOf(item, 'code'),
        available: boolOf(item, 'available') ?? false,
        classIndex: numberOrNull(item.class_index),
        probability: numberOrNull(item.probability),
        method: strOf(item, 'method'),
        baseline: strOf(item, 'baseline'),
        steps: numOf(item, 'steps') ?? 0,
        signedScore: numberOrNull(item.signed_score),
        logitDelta: numberOrNull(item.logit_delta),
        probabilityDeltaRaw: numberOrNull(item.probability_delta_raw),
        completenessError: numberOrNull(item.completeness_error),
        normalized: boolOf(item, 'normalized') ?? false,
        degenerate: boolOf(item, 'degenerate') ?? false,
        leads: objectsOf(item, 'leads').map(parseAttributionLead),
        timeSegments: objectsOf(item, 'time_segments').map(parseAttributionSegment),
        topLeads: objectsOf(item, 'top_leads').map(parseAttributionLead),
        topTimeSegments: objectsOf(item, 'top_time_segments').map(parseAttributionSegment),
        occlusionCheck: parseOcclusionCheck(item.occlusion_check),
        fallbackReason: strOf(item, 'fallback_reason'),
        reason: strOf(item, 'reason'),
    };
}

function parseAttribution(raw: JsonObject): EcgAttribution {
    const attribution = isObject(raw.attribution) ? raw.attribution : {};
    const completeness = isObject(attribution.completeness)
        ? attribution.completeness
        : null;
    return {
        available: boolOf(attribution, 'available') ?? false,
        version: strOf(attribution, 'version'),
        method: strOf(attribution, 'method'),
        baseline: strOf(attribution, 'baseline'),
        steps: numOf(attribution, 'steps') ?? 0,
        normalized: boolOf(attribution, 'normalized') ?? false,
        degenerate: boolOf(attribution, 'degenerate') ?? false,
        signedScore: numberOrNull(attribution.signed_score),
        completeness: completeness
            ? {
                meanAbsError: numberOrNull(completeness.mean_abs_error),
                maxAbsError: numberOrNull(completeness.max_abs_error),
            }
            : null,
        leads: objectsOf(attribution, 'leads').map(parseAttributionLead),
        timeSegments: objectsOf(attribution, 'time_segments').map(parseAttributionSegment),
        topLeads: objectsOf(attribution, 'top_leads').map(parseAttributionLead),
        topTimeSegments: objectsOf(attribution, 'top_time_segments').map(parseAttributionSegment),
        findings: objectsOf(attribution, 'findings').map(parseAttributionFinding),
        reason: strOf(attribution, 'reason'),
    };
}

function parseEvidenceItem(item: JsonObject): EcgEvidenceItem {
    const result: EcgEvidenceItem = {
        source: strOf(item, 'source'),
        code: strOf(item, 'code'),
        text: strOf(item, 'text'),
        available: boolOf(item, 'available') ?? false,
    };
    const status = strOf(item, 'status');
    if (status) result.status = status;
    const method = strOf(item, 'method');
    if (method) result.method = method;
    const probability = numberOrNull(item.probability);
    if (probability !== null) result.probability = probability;
    const threshold = numberOrNull(item.threshold);
    if (threshold !== null) result.threshold = threshold;
    const thresholdMargin = numberOrNull(item.threshold_margin);
    if (thresholdMargin !== null) result.thresholdMargin = thresholdMargin;
    if (Array.isArray(item.top_leads)) {
        result.topLeads = item.top_leads.filter(isObject).map(parseAttributionLead);
    }
    if (Array.isArray(item.top_time_segments)) {
        result.topTimeSegments = item.top_time_segments
            .filter(isObject)
            .map(parseAttributionSegment);
    }
    const check = parseOcclusionCheck(item.occlusion_check);
    if (check) result.occlusionCheck = check;
    return result;
}

function parseFindingRationale(item: JsonObject): EcgFindingRationale {
    return {
        code: strOf(item, 'code'),
        available: boolOf(item, 'available') ?? false,
        summary: strOf(item, 'summary'),
        evidence: objectsOf(item, 'evidence').map(parseEvidenceItem),
        reason: strOf(item, 'reason'),
    };
}

function parseRationale(raw: JsonObject): EcgRationale {
    const rationale = isObject(raw.rationale) ? raw.rationale : {};
    return {
        version: strOf(rationale, 'version'),
        available: boolOf(rationale, 'available') ?? false,
        summary: strOf(rationale, 'summary'),
        evidence: objectsOf(rationale, 'evidence').map(parseEvidenceItem),
        findings: objectsOf(rationale, 'findings').map(parseFindingRationale),
        reason: strOf(rationale, 'reason'),
    };
}

function parseExplainabilityFinding(item: JsonObject): EcgFinding {
    return {
        code: strOf(item, 'code') || '—',
        probability: numOf(item, 'probability'),
        probabilityRaw: null,
        threshold: numOf(item, 'threshold'),
        thresholdMargin: numOf(item, 'threshold_margin'),
        source: strOf(item, 'source'),
        calibrated: booleanOrNull(item.calibrated),
        ...emptyFindingExtras(),
    };
}

function parseAnalysisFinding(finding: JsonObject): EcgFinding {
    const miConfirm = isObject(finding.mi_confirm) ? finding.mi_confirm : null;
    return {
        code: strOf(finding, 'code') || '—',
        // Анализатор отдаёт `prob`; `probability` принимаем для совместимости
        // с ранними ответами — без подстановки выдуманного значения.
        probability: numOf(finding, 'prob') ?? numOf(finding, 'probability'),
        probabilityRaw: numOf(finding, 'prob_raw'),
        threshold: numOf(finding, 'threshold'),
        thresholdMargin: numOf(finding, 'threshold_margin'),
        source: strOf(finding, 'source'),
        calibrated: boolOf(finding, 'calibrated'),
        phase: strOf(finding, 'phase'),
        confidenceNote: strOf(finding, 'confidence_note'),
        conflictNote: strOf(finding, 'conflict_note'),
        miScreening: parseMiStage(finding.mi_screening),
        miConfirm: parseMiConfirmStage(finding.mi_confirm),
        hypSource: strOf(finding, 'hyp_source'),
        hypCriteria: parseHypCriteria(finding.hyp_criteria),
        // ST-детектор кладётся в находку MI отдельным блоком (`st_criteria`),
        // в старых ответах — внутри `mi_confirm.rules`.
        st: parseStCriteria(finding.st_criteria) ?? parseStCriteria(miConfirm?.rules),
    };
}

function parseExplainability(raw: JsonObject): EcgExplainability | null {
    if (!isObject(raw.explainability)) return null;
    const explain = raw.explainability;
    const required = isObject(explain.required_model_input) ? explain.required_model_input : {};
    const findingsRaw = Array.isArray(explain.findings) ? explain.findings.filter(isObject) : [];
    return {
        version: strOf(explain, 'version'),
        criteriaVersion: strOf(explain, 'criteria_version'),
        confidenceVersion: strOf(explain, 'confidence_version'),
        attributionVersion: strOf(explain, 'attribution_version'),
        rationaleVersion: strOf(explain, 'rationale_version'),
        kind: strOf(explain, 'kind'),
        attributionKind: strOf(explain, 'attribution_kind'),
        diagnosis: boolOf(explain, 'diagnosis') ?? false,
        status: strOf(explain, 'status'),
        reason: strOf(explain, 'reason'),
        criteria: parseCriteria(explain),
        measurements: parseMeasurementSummary(explain),
        signalQuality: parseSignalQuality(explain),
        confidence: parseConfidence(explain),
        attribution: parseAttribution(explain),
        rationale: parseRationale(explain),
        findings: findingsRaw.map(parseExplainabilityFinding),
        limitations: stringArrayOf(explain, 'limitations'),
        requiredModelInput: {
            leads: numberOrNull(required.leads),
            durationS: numberOrNull(required.duration_s),
        },
    };
}

/**
 * parseEcgAnalysis — строгий разбор ответа сервиса (без `any`): неожидаемая
 * структура — явная ошибка, а не пустые поля «по умолчанию».
 */
export function parseEcgAnalysis(raw: unknown): EcgAnalysis {
    if (!isObject(raw)) throw new Error('Сервис вернул неожиданный ответ');
    const quality = isObject(raw.quality) ? raw.quality : {};
    const signal = isObject(raw.signal) ? raw.signal : {};
    const measurements = isObject(raw.measurements) ? raw.measurements : {};
    const findings: EcgFinding[] = Array.isArray(raw.findings)
        ? raw.findings.filter(isObject).map(parseAnalysisFinding)
        : [];
    return {
        status: strOf(raw, 'status'),
        reason: strOf(raw, 'reason'),
        quality: {
            verdict: strOf(quality, 'verdict'),
            leadsExtracted: numOf(quality, 'leads_extracted'),
            pxPerMm: numOf(quality, 'px_per_mm'),
            calibrationFound: boolOf(quality, 'calibration_found'),
            sharpness: numOf(quality, 'sharpness'),
            exposure: numOf(quality, 'exposure'),
            coverage: numOf(quality, 'coverage'),
        },
        previewPngBase64: strOf(signal, 'preview_png_b64'),
        signal: parseSignal(signal),
        measurements: {
            hrBpm: numOf(measurements, 'hr_bpm'),
            prMs: numOf(measurements, 'pr_ms'),
            qrsMs: numOf(measurements, 'qrs_ms'),
            qtMs: numOf(measurements, 'qt_ms'),
            qtcMs: numOf(measurements, 'qtc_ms'),
            axisDeg: numOf(measurements, 'axis_deg'),
            availability: boolMapOf(measurements, 'availability'),
        },
        findings,
        model: parseModel(raw),
        explainability: parseExplainability(raw),
        miHyp: parseMiHypBlock(raw),
        pipelineVersion: strOf(raw, 'pipeline_version'),
    };
}

/**
 * analyzeEcgFile — отправить снимок на сервер платформы и разобрать ответ.
 * Транспортные сбои пробрасываются как есть: вызывающий сам решает, класть ли
 * снимок в офлайн-очередь (это НЕ ошибка данных).
 */
export async function analyzeEcgFile(file: File): Promise<EcgAnalysis> {
    const data = new Uint8Array(await file.arrayBuffer());
    const response = await getSDK().ecg.analyzeEcg(
        {
            data,
            fileName: file.name,
            contentType: file.type,
        },
        { timeoutMs: ECG_ANALYZE_TIMEOUT_MS },
    );
    let raw: unknown;
    try {
        raw = JSON.parse(response.resultJson);
    } catch {
        throw new Error('Сервис разбора вернул некорректный ответ');
    }
    return parseEcgAnalysis(raw);
}

/**
 * describeAnalyzeError — человеческий текст для сбоя серверного разбора.
 * Таймаут и транспортный сбой отличаются от серверного ответа (например,
 * «сервис разбора недоступен») — серверный текст показывается как есть.
 */
export function describeAnalyzeError(error: unknown): string {
    if (isAnalyzeTimeout(error)) {
        return `Сервер не ответил за ${Math.round(ECG_ANALYZE_TIMEOUT_MS / 1000)} секунд — повторите разбор`;
    }
    if (isTransportError(error)) {
        return 'Сервер разбора недоступен — проверьте подключение к серверу';
    }
    return friendlyError(error);
}
